/**
 * SnackbarProvider Component
 * Provider for displaying toast notifications
 */

import { Snackbar, Alert, AlertTitle } from '@mui/material';
import { useSnackbarStore } from './useSnackbar';

export interface SnackbarProviderProps {
  children: React.ReactNode;
}

/**
 * SnackbarProvider - Provider for toast notifications
 * Add this to your app root to enable snackbars
 *
 * @example
 * ```tsx
 * <SnackbarProvider>
 *   <App />
 * </SnackbarProvider>
 * ```
 */
export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const { snackbars, removeSnackbar } = useSnackbarStore();

  return (
    <>
      {children}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open={true}
          onClose={() => removeSnackbar(snackbar.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            // Stack multiple snackbars vertically
            transform: `translateY(${index * -70}px)`,
          }}
        >
          <Alert
            onClose={() => removeSnackbar(snackbar.id)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
            role="alert"
            aria-live={snackbar.severity === 'error' ? 'assertive' : 'polite'}
          >
            {snackbar.description ? (
              <>
                <AlertTitle>{snackbar.message}</AlertTitle>
                {snackbar.description}
              </>
            ) : (
              snackbar.message
            )}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
}
